<script lang="ts">  import { m } from '$lib/paraglide/messages';

  import { translation } from '$lib/locale/locale';
  import type { Track } from '$lib/models/track';
  import { assetLink, AssetType, downloadAsset } from '$lib/utils/asset';
  import downloadIcon from '$lib/assets/shared/download.svg';
  import loadingIcon from '$lib/assets/shared/loading-black.svg';
  import arrowIcon from '$lib/assets/shared/right-arrow.svg';
  import type { User } from '$lib/models/user';
  import { api } from '$lib/api';
  import { error } from '@sveltejs/kit';
  import Spinner from '../shared/spinner.svelte';
  import { onDestroy } from 'svelte';
  import { showTrialModal } from '$lib/stores/modals';
  import { canDownload } from '$lib/utils/download';
  import { browser } from '$app/environment';
  import { purchaseModal } from '$lib/state/purchase-modal.svelte';
  import { purchaseSfxModal } from '$lib/state/purchase-sfx-modal.svelte';
  import { attributeUsModal } from '$lib/state/attribute-us-modal.svelte';

  let versions: Track[] = [];
  let mainVersion: Track | null = null;
  export let user: User | null | void;
  const { langHref } = translation();

  let dropdownVisible = false;

  let loadingVersionId: number;
  export let track: Track;
  export let freeMusicModal = false;
  let loading = false;
  let loadingVersions = false;
  let timer: any;

  let trackLink = assetLink(langHref, track, track.type);

  async function dropdownClick() {
    if (!canDownload(track, user)) {
      if (track.type === AssetType.Track) {
        if (freeMusicModal) {
          attributeUsModal.show(track, !user);
        } else {
          purchaseModal.show(track);
        }
      } else if (track.type === AssetType.SFX) {
        purchaseSfxModal.show(track);
      } else {
        showTrialModal.set(true);
      }
      return;
    }

    dropdownVisible = !dropdownVisible;
    if (loadingVersions) {
      return;
    }
    if (+track.versions > 1 && versions.length === 0) {
      loadingVersions = true;
      dropdownVisible = true;
      let endpoint = '';
      if (track.type === AssetType.Intro) {
        endpoint = `/v1/intros/${track.id}?slug=${track.slug}`;
      } else if (track.type === AssetType.Track) {
        endpoint = `/v1/tracks/${track.id}?slug=${track.slug}`;
      } else {
        throw new Error(`Asset type not supported`);
      }
      let assetFetch: Track | undefined;
      try {
        assetFetch = await api<Track>(fetch, endpoint);
        versions = assetFetch.mp3Versions;
        mainVersion = assetFetch.mp3MainVersion;
      } catch (e) {
        error(404, {
          id: '404',
          message: 'Not found',
        });
      } finally {
        loadingVersions = false;
      }
    } else if (+track.versions <= 1) {
      download(track);
    }
  }

  function download(asset: Track | null) {
    if (!asset) {
      return;
    }

    if (canDownload(asset, user)) {
      if (loadingVersionId === track.id) {
        return;
      }
      loadingVersionId = asset.id;
      loading = true;
      clearTimeout(timer);
      timer = setTimeout(() => {
        loading = false;
        loadingVersionId = 0;
      }, 5000);
      downloadAsset(asset).then((_) => {});
    } else {
      // If user is not subscribed, open the purchase modal/subscription modal
      if (track.type === AssetType.Track) {
        if (freeMusicModal) {
          attributeUsModal.show(track, !user);
        } else {
          purchaseModal.show(track);
        }
      } else if (track.type === AssetType.SFX) {
        purchaseSfxModal.show(track);
      } else {
        showTrialModal.set(true);
      }
    }
  }

  if (browser) {
    onDestroy(() => {
      if (timer) {
        clearTimeout(timer);
      }
    });
  }
</script>

{#if +track.versions > 1}
  {#if dropdownVisible}
    <button class="backdrop" on:click={() => (dropdownVisible = false)} aria-label={m['actions.download']()}></button>
  {/if}
  <div class="dropdown">
    <button on:click={dropdownClick} data-tooltip={m['actions.download']()} class="icon small download-button">
      <img
        class=" img_text-secondary"
        src={loadingVersionId === track.id ? loadingIcon : downloadIcon}
        class:rotate={loadingVersionId === track.id}
        alt={m['actions.download']()}
        loading="lazy"
      />
    </button>

    {#if dropdownVisible}
      <div class="dropdown-menu dropdown-menu-right">
        <button
          class="dropdown-item"
          on:click={() => {
            download(track);
          }}
        >
          <div>{m['track-details.all-versions']()}</div>
          <img
            class="small-icon img_text-secondary"
            src={loadingVersionId === track.id ? loadingIcon : downloadIcon}
            class:rotate={loadingVersionId === track.id}
            alt={m['actions.download']()}
            loading="lazy"
          />
        </button>

        <!-- Show loader while versions are loading -->
        {#if loadingVersions}
          <div class="dropdown-item loading-versions">
            <Spinner size={'20px'} micro={true} />
            <div class="loading-versions-text">
              {m['search.loading-versions']()}
            </div>
          </div>
        {/if}

        <!-- Show versions when available -->
        {#if versions && versions.length > 0}
          {#if mainVersion}
            <button class="dropdown-item" on:click={() => download(mainVersion)}>
              <div>{m['track-details.main-version']()}</div>
              <img
                class="small-icon img_text-secondary"
                src={loadingVersionId === mainVersion.id ? loadingIcon : downloadIcon}
                class:rotate={loadingVersionId === mainVersion.id}
                alt={m['actions.download']()}
              />
            </button>
          {/if}

          {#each versions.slice(0, 3) as version}
            <button class="dropdown-item" on:click={() => download(version)}>
              <div>{version.title}</div>
              <img
                class="small-icon img_text-secondary"
                src={loadingVersionId === version.id ? loadingIcon : downloadIcon}
                class:rotate={loadingVersionId === version.id}
                alt={m['actions.download']()}
              />
            </button>
          {/each}
          {#if versions.length > 3}
            <a class="dropdown-item" href={trackLink}>
              <div>{m['track-details.more-versions']()}</div>
              <img class="small-icon arrow" src={arrowIcon} alt="" aria-hidden="true" />
            </a>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
  <!-- For tracks with no alternative versions and SFX, don't show dropdown (SFX don't have versions)-->
{:else if +track.versions <= 1 || track.type === AssetType.SFX}
  <button on:click={() => download(track)} data-tooltip={m['actions.download']()} class="icon small download-button">
    <img
      class="img_text-secondary"
      src={loading ? loadingIcon : downloadIcon}
      class:rotate={loadingVersionId === track.id}
      alt={m['actions.download']()}
      loading="lazy"
    />
  </button>
{/if}

<style>
  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 500;
  }
  .download-button {
    margin-left: 16px;
  }

  .dropdown-menu {
    display: block;
    font-size: var(--xs-font-size);
    position: absolute !important;
    padding: 8px 12px;
    border-radius: 12px;
    z-index: 502;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
  }

  .icon {
    flex-shrink: 0;
    height: 24px;
    width: 24px;
  }

  .icon img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  .small-icon {
    width: 16px;
    height: 16px;
    margin-left: 8px;
  }

  .dropdown-item:hover,
  .dropdown-item:focus {
    background-color: #f6f7f8;
    color: var(--shimmer-color);
    border-radius: 8px;
    padding: 8px 12px;
  }

  .loading-versions:hover {
    background-color: transparent;
  }
  .loading-versions-text {
    margin-left: 8px;
  }

  .arrow {
    filter: brightness(0) invert(16%) sepia(18%) saturate(411%) hue-rotate(173deg) brightness(90%) contrast(94%);
  }

  @media (max-width: 900px) {
    .download-button {
      margin-left: 10px;
    }
  }
</style>
